/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, January 4th 2023, 11:57:00 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import SettingsIcon from "@mui/icons-material/Settings";
import { TaskTemplate } from "lib/schemas";
import { useWorkflowContext } from "service-builder/providers";
import { useWorkflowStore, getUserTaskTypesData } from "service-builder/stores";
import { DataTable, HeadCell, OSIconButton, StyledOSDialog } from "ui-widgets";
import { TaskAccessSettings } from "./TaskAccessSettings";
import { getUserTaskTypeWidgets } from "./TaskTypeWidgets";

const headCells: HeadCell<TaskTemplate, "user_task_type_name">[] = [
  {
    id: "title",
    numeric: false,
    disablePadding: true,
    label: "Task",
  },
  {
    id: "user_task_type_name",
    numeric: false,
    disablePadding: true,
    label: "User Task Type",
  },
];

export const ManageTaskPermissions = () => {
  const [selectedTaskId, setSelectedTaskId] = React.useState<TaskTemplate["id"]>(null);
  const {
    taskTemplateListContext: {
      itemsListState: { savedItems: taskTemplates },
    },
  } = useWorkflowContext();
  const workerTaskTemplates = taskTemplates.filter((t) => t.type === "View" && !t.client_task);

  const userTaskTypeWidgets = getUserTaskTypeWidgets(useWorkflowStore(getUserTaskTypesData));

  return (
    <>
      <DataTable<TaskTemplate, "user_task_type_name">
        title="Worker Tasks"
        headCells={headCells}
        defaultOrderBy="sequence"
        initialRowsPerPage={10}
        dataSource={workerTaskTemplates}
        uniqueKey="id"
        noDataMessage={"You cannot manage task access because your workflow has no configured tasks for workers."}
        searchKeys={null}
        unit="Worker Task"
        titleKey="title"
        renderRowActions={(taskTemplate) => (
          <>
            <OSIconButton
              onClick={() => setSelectedTaskId(taskTemplate.id)}
              toolTipText={<>Manage Access to {taskTemplate.title}</>}
              icon={<SettingsIcon color="primary" />}
            />
            <StyledOSDialog
              popUpIcon={<GroupsIcon color="secondary" />}
              popUpTitle={`Manage Team Access for ${taskTemplate.title}`}
              open={selectedTaskId && selectedTaskId === taskTemplate.id}
              onClose={() => setSelectedTaskId(null)}
              className="os-task-team-access-modal"
            >
              <TaskAccessSettings taskTemplate={taskTemplate} />
            </StyledOSDialog>
          </>
        )}
        listViewMode="table"
        allowRowSelection={false}
        cellRenderProps={{
          user_task_type_name: (taskTemplate) => (
            <>{userTaskTypeWidgets[taskTemplate.user_action_type]?.userTaskTypeTitle ?? "N/A"}</>
          ),
        }}
      />
    </>
  );
};
